// src/components/common/DataTable.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const DataTable = ({
  data = [],
  columns = [],
  filterConfig = [],
  title = '',
  subtitle = '',
  loading = false,
  defaultSort = { key: '', direction: 'desc' },
  defaultItemsPerPage = 10,
  itemsPerPageOptions = [10, 25, 50, 100],
  showSearch = true,
  showFilters = true,
  showPagination = true,
  emptyMessage = 'No data available',
  emptyIcon = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Initialize filters when filterConfig changes — must be useEffect, not useMemo,
  // because calling setState inside useMemo causes an infinite render loop.
  useEffect(() => {
    const init = {};
    filterConfig.forEach((f) => {
      init[f.key] = f.type === 'multiselect' ? [] : null;
    });
    setFilters(init);
  }, [filterConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterOptions = useMemo(() => {
    const opts = {};
    filterConfig.forEach((f) => {
      opts[f.key] =
        f.options === 'auto'
          ? [...new Set(data.map((d) => d[f.key]))]
              .filter((v) => v != null && v !== '')
              .sort()
          : f.options || [];
    });
    return opts;
  }, [data, filterConfig]);

  const searchableCols = useMemo(
    () => columns.filter((c) => c.searchable !== false).map((c) => c.key),
    [columns],
  );

  const filteredData = useMemo(() => {
    let out = [...data];

    if (searchTerm && searchableCols.length > 0) {
      const s = searchTerm.toLowerCase();
      out = out.filter((row) =>
        searchableCols.some((k) => String(row[k] ?? '').toLowerCase().includes(s)),
      );
    }

    Object.entries(filters).forEach(([key, val]) => {
      const def = filterConfig.find((f) => f.key === key);
      if (!def) return;
      if (def.type === 'multiselect' && Array.isArray(val) && val.length > 0)
        out = out.filter((r) => val.includes(r[key]));
      else if ((def.type === 'radio' || def.type === 'checkbox') && val !== null)
        out = out.filter((r) => r[key] === val);
    });

    return out;
  }, [data, searchTerm, filters, searchableCols, filterConfig]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      let av = a[sortConfig.key];
      let bv = b[sortConfig.key];

      if (
        sortConfig.key.toLowerCase().includes('date') ||
        (typeof av === 'string' && /^\d{4}-\d{2}-\d{2}/.test(av))
      ) {
        const parse = (s) => {
          if (!s) return new Date(0);
          const [y, m, d] = s.split('T')[0].split('-');
          return new Date(+y, +m - 1, +d);
        };
        av = parse(av);
        bv = parse(bv);
      }

      if (typeof av === 'boolean') { av = av ? 1 : 0; bv = bv ? 1 : 0; }
      if (av == null) av = '';
      if (bv == null) bv = '';

      if (av instanceof Date) return sortConfig.direction === 'asc' ? av - bv : bv - av;
      if (typeof av === 'string')
        return sortConfig.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortConfig.direction === 'asc' ? av - bv : bv - av;
    });
  }, [filteredData, sortConfig]);

  // Reset to page 1 when search/filters/sort change — useEffect, not useMemo.
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filters, sortConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const start = (currentPage - 1) * itemsPerPage;
  const pageData = showPagination ? sortedData.slice(start, start + itemsPerPage) : sortedData;

  const handleSort = (key) => {
    if (columns.find((c) => c.key === key)?.sortable === false) return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleFilterChange = (key, val) => {
    const def = filterConfig.find((f) => f.key === key);
    if (!def) return;
    setFilters((prev) => {
      if (def.type === 'radio' || def.type === 'checkbox') return { ...prev, [key]: val };
      if (def.type === 'multiselect') {
        const cur = prev[key] || [];
        return {
          ...prev,
          [key]: cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val],
        };
      }
      return prev;
    });
  };

  const clearFilters = () => {
    const cleared = {};
    filterConfig.forEach((f) => { cleared[f.key] = f.type === 'multiselect' ? [] : null; });
    setFilters(cleared);
    setSearchTerm('');
  };

  const hasActiveFilters =
    !!searchTerm ||
    Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : v !== null));

  const activeCount = Object.values(filters).reduce(
    (n, v) => n + (Array.isArray(v) ? v.length : v !== null ? 1 : 0),
    0,
  );

  if (loading) {
    return (
      <div className="bg-white border border-grey-200 rounded-xl p-8 animate-pulse">
        <div className="h-4 bg-grey-200 rounded w-1/4 mb-4" />
        <div className="h-32 bg-grey-200 rounded" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-grey-200 rounded-xl overflow-hidden">

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      {(title || subtitle || showSearch || (showFilters && filterConfig.length > 0)) && (
        <div className="bg-grey-50 px-4 py-3 md:px-6 border-b border-grey-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {(title || subtitle) && (
              <div>
                {title && (
                  <h3 className="text-sm font-semibold text-darkblue-500">{title}</h3>
                )}
                {subtitle && (
                  <p className="text-xs text-grey-500 mt-0.5">
                    {subtitle} — {sortedData.length} of {data.length} records
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 flex-shrink-0">
              {showSearch && (
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-3 py-1.5 border border-grey-300 rounded-lg text-sm text-darkblue-500 bg-white outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
                  />
                </div>
              )}

              {showFilters && filterConfig.length > 0 && (
                <button
                  onClick={() => setShowFilterPanel(!showFilterPanel)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    hasActiveFilters
                      ? 'bg-brightblue-50 border-brightblue-200 text-brightblue-600'
                      : 'bg-white border-grey-300 text-grey-600 hover:bg-grey-50'
                  }`}
                >
                  <FunnelIcon className="h-4 w-4" />
                  Filters
                  {activeCount > 0 && (
                    <span className="px-1.5 py-0.5 bg-brightblue-500 text-white text-xs rounded-full leading-none">
                      {activeCount}
                    </span>
                  )}
                </button>
              )}

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-grey-500 hover:text-darkblue-500 underline"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Filter panel */}
          {showFilterPanel && showFilters && filterConfig.length > 0 && (
            <div className="mt-3 p-4 bg-white rounded-lg border border-grey-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-darkblue-500 uppercase tracking-widest">
                  Filter options
                </h4>
                <button
                  onClick={() => setShowFilterPanel(false)}
                  className="text-xs text-grey-500 hover:text-darkblue-500 flex items-center gap-1"
                >
                  Hide <ChevronUpIcon className="h-3 w-3" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {filterConfig.map((filter) => (
                  <div key={filter.key}>
                    <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">
                      {filter.label}
                    </label>
                    {filter.type === 'multiselect' && (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {filterOptions[filter.key]?.map((opt) => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filters[filter.key]?.includes(opt)}
                              onChange={() => handleFilterChange(filter.key, opt)}
                              className="rounded border-grey-300 text-brightblue-500 focus:ring-brightblue-300"
                            />
                            <span className="text-sm text-darkblue-500">{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {filter.type === 'radio' && (
                      <div className="space-y-1.5">
                        {filterOptions[filter.key]?.map((opt) => (
                          <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={filters[filter.key] === opt.value}
                              onChange={() => handleFilterChange(filter.key, opt.value)}
                              className="border-grey-300 text-brightblue-500 focus:ring-brightblue-300"
                            />
                            <span className="text-sm text-darkblue-500">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {pageData.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-grey-200">
              <thead className="bg-darkblue-500">
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => col.sortable !== false && handleSort(col.key)}
                      className={`px-4 py-3 text-${col.align || 'left'} text-xs font-bold text-white uppercase tracking-widest whitespace-nowrap ${
                        col.sortable !== false
                          ? 'cursor-pointer hover:bg-darkblue-600 select-none'
                          : ''
                      }`}
                    >
                      <div
                        className={`flex items-center gap-1 ${
                          col.align === 'center' ? 'justify-center' : col.align === 'right' ? 'justify-end' : ''
                        }`}
                      >
                        {col.label}
                        {col.sortable !== false && sortConfig.key === col.key && (
                          sortConfig.direction === 'asc'
                            ? <ChevronUpIcon className="w-3.5 h-3.5" />
                            : <ChevronDownIcon className="w-3.5 h-3.5" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-100">
                {pageData.map((row, i) => (
                  <tr
                    key={row.id || i}
                    className={i % 2 === 0 ? 'bg-white' : 'bg-grey-50'}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 text-sm text-darkblue-500 text-${col.align || 'left'} whitespace-nowrap`}
                      >
                        {col.render ? col.render(row[col.key], row) : row[col.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ─────────────────────────────────────────────── */}
          {showPagination && (
            <div className="bg-white px-4 py-3 md:px-6 border-t border-grey-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-xs text-grey-500">
                <span>
                  Showing <span className="font-medium text-darkblue-500">{start + 1}</span>–
                  <span className="font-medium text-darkblue-500">
                    {Math.min(start + itemsPerPage, sortedData.length)}
                  </span>{' '}
                  of <span className="font-medium text-darkblue-500">{sortedData.length}</span>
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(+e.target.value); setCurrentPage(1); }}
                  className="border border-grey-300 rounded-lg px-2 py-1 text-xs text-darkblue-500 focus:outline-none focus:ring-2 focus:ring-brightblue-300"
                >
                  {itemsPerPageOptions.map((n) => (
                    <option key={n} value={n}>{n} / page</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-grey-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-grey-50 transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4 text-darkblue-500" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span className="px-1 text-xs text-grey-400">…</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                          currentPage === p
                            ? 'bg-brand-500 text-white border-brand-500'
                            : 'border-grey-300 text-darkblue-500 hover:bg-grey-50'
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-grey-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-grey-50 transition-colors"
                >
                  <ChevronRightIcon className="h-4 w-4 text-darkblue-500" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="p-10 text-center">
          {emptyIcon && <div className="mb-3 flex justify-center">{emptyIcon}</div>}
          <p className="text-sm text-grey-500">
            {hasActiveFilters ? 'No results match your filters.' : emptyMessage}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 text-xs text-brightblue-600 hover:text-brightblue-700 font-medium underline"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DataTable;